import React from "react";
import {
  FiAlertTriangle,
  FiArrowRightCircle,
  FiBarChart2,
  FiBookOpen,
  FiClock,
  FiRotateCcw,
} from "react-icons/fi";

function tsToDate(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return new Date(ts > 1e12 ? ts : ts * 1000);
  if (typeof ts === "string") return new Date(ts);
  if (typeof ts === "object") {
    if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
    if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
    if (typeof ts.toDate === "function") return ts.toDate();
  }
  return null;
}

const displayText = (value, fallback = "—") => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.vi || value.en || fallback;
  return String(value);
};

export default function AssignmentCard({ assignment, status, classInfo, onStart, onOpenHistory }) {
  const due = tsToDate(assignment?.deadline);
  const dueStr = due ? due.toLocaleString("vi-VN") : "—";

  const isOverdue =
    due &&
    !assignment?.allowLate &&
    Date.now() > new Date(due).setHours(23, 59, 59, 999);

  const badge =
    status?.type === "submitted"
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : status?.type === "inprogress"
      ? "bg-amber-100 text-amber-700 border-amber-200"
      : "bg-slate-100 text-slate-700 border-slate-200";

  const bestPct =
    typeof status?.bestPercent === "number"
      ? status.bestPercent
      : typeof status?.best?.percent === "number"
      ? status.best.percent
      : null;

  const disabled = status?.canStart === false;
  const reason = status?.blockReason || "";
  const attemptCount = status?.attempts || 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium ${badge}`}>
              {status?.label || "Chưa làm"}
            </span>

            {isOverdue && (
              <span className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
                <FiAlertTriangle /> Hết hạn
              </span>
            )}

            {classInfo && (
              <span className="inline-flex max-w-full items-center gap-1 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                <FiBookOpen className="shrink-0" />
                <span className="truncate">{displayText(classInfo.name, "Lớp học")}</span>
              </span>
            )}
          </div>

          <h3 className="mt-2 text-lg font-semibold leading-snug text-slate-900">
            {displayText(assignment?.title, "Untitled assignment")}
          </h3>

          {assignment?.description && (
            <p className="mt-1 line-clamp-2 text-sm text-slate-600">
              {displayText(assignment.description)}
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-700">
            <span className="inline-flex items-center gap-1">
              <FiClock className="opacity-80" />
              Hạn: <span className="font-medium">{dueStr}</span>
            </span>

            {typeof assignment?.maxAttempts === "number" && assignment.maxAttempts > 0 && (
              <span className="text-slate-500">
                Lượt tối đa: <span className="font-medium text-slate-700">{assignment.maxAttempts}</span>
              </span>
            )}

            {bestPct !== null && (
              <span className="inline-flex items-center gap-1">
                <FiBarChart2 className="opacity-80" />
                Điểm cao nhất: <span className="font-medium">{bestPct}%</span>
              </span>
            )}
          </div>

          {disabled && reason && (
            <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
              <FiAlertTriangle /> {reason}
            </div>
          )}
        </div>

        <div className="flex w-full flex-row gap-2 lg:w-44 lg:flex-col shrink-0">
          <button
            type="button"
            onClick={onStart}
            disabled={disabled}
            title={disabled ? reason : ""}
            className={`flex-1 rounded-xl px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${
              disabled
                ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            <FiArrowRightCircle />
            <span>{status?.type === "inprogress" ? "Tiếp tục" : "Bắt đầu"}</span>
          </button>

          <button
            type="button"
            onClick={onOpenHistory}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 inline-flex items-center justify-center gap-2 hover:bg-slate-50 transition"
          >
            <FiRotateCcw />
            <span>Lịch sử ({attemptCount})</span>
          </button>
        </div>
      </div>
    </div>
  );
}
